import React, { createContext, useContext, useState, useCallback } from 'react'
import { Product, CartItem, OrderType } from '../types'
import toast from 'react-hot-toast'

interface CartContextType {
  items: CartItem[]
  /** اقلام یک سمت معامله (خرید یا فروش) */
  itemsOf: (mode: OrderType) => CartItem[]
  addItem: (product: Product, quantity: number, mode: OrderType) => void
  removeItem: (productId: string, mode: OrderType) => void
  updateQuantity: (productId: string, mode: OrderType, quantity: number) => void
  /** پاک کردن کل سبد (هر دو سمت) */
  clearCart: () => void
  /** پاک کردن یک سمت معامله (بعد از ثبت سفارش) */
  clearMode: (mode: OrderType) => void
  countOf: (mode: OrderType) => number
  subtotalOf: (mode: OrderType) => number
  shippingOf: (mode: OrderType) => number
  totalOf: (mode: OrderType) => number
  /** قیمت واحد یک محصول در سمت مشخص */
  unitPriceOf: (product: Product, mode: OrderType) => number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

const STORAGE_KEY = 'gold-silver-cart'

const readStored = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return []
    const parsed = JSON.parse(stored)
    if (!Array.isArray(parsed)) return []
    // داده‌های قدیمی که فیلد mode نداشتند، خرید در نظر گرفته می‌شوند
    return parsed
      .filter((i: any) => i && i.product)
      .map((i: any) => ({
        product: i.product,
        quantity: Number(i.quantity) || 1,
        mode: i.mode === 'SELL' ? 'SELL' : 'BUY'
      })) as CartItem[]
  } catch {
    return []
  }
}

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(readStored)

  const persist = useCallback((newItems: CartItem[]) => {
    setItems(newItems)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newItems))
  }, [])

  const itemsOf = useCallback(
    (mode: OrderType) => items.filter((i) => i.mode === mode),
    [items]
  )

  const addItem = useCallback(
    (product: Product, quantity = 1, mode: OrderType = 'BUY') => {
      setItems((prev) => {
        const idx = prev.findIndex((i) => i.product.id === product.id && i.mode === mode)
        let newItems: CartItem[]

        if (idx >= 0) {
          newItems = [...prev]
          newItems[idx] = { ...newItems[idx], quantity: newItems[idx].quantity + quantity }
        } else {
          newItems = [...prev, { product, quantity, mode }]
        }

        localStorage.setItem(STORAGE_KEY, JSON.stringify(newItems))
        toast.success(
          mode === 'BUY'
            ? `«${product.name}» به سبد خرید شما اضافه شد`
            : `«${product.name}» به سبد فروش شما اضافه شد`
        )
        return newItems
      })
    },
    []
  )

  const removeItem = useCallback((productId: string, mode: OrderType) => {
    setItems((prev) => {
      const newItems = prev.filter((i) => !(i.product.id === productId && i.mode === mode))
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newItems))
      return newItems
    })
  }, [])

  const updateQuantity = useCallback(
    (productId: string, mode: OrderType, quantity: number) => {
      if (quantity <= 0) {
        removeItem(productId, mode)
        return
      }

      setItems((prev) => {
        const newItems = prev.map((i) =>
          i.product.id === productId && i.mode === mode ? { ...i, quantity } : i
        )
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newItems))
        return newItems
      })
    },
    [removeItem]
  )

  const clearCart = useCallback(() => {
    persist([])
  }, [persist])

  const clearMode = useCallback((mode: OrderType) => {
    setItems((prev) => {
      const newItems = prev.filter((i) => i.mode !== mode)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newItems))
      return newItems
    })
  }, [])

  const unitPriceOf = useCallback((product: Product, mode: OrderType) => {
    const value = mode === 'BUY' ? product.sellingPrice : product.buybackPrice
    return value ?? 0
  }, [])

  const countOf = useCallback(
    (mode: OrderType) => itemsOf(mode).reduce((sum, i) => sum + i.quantity, 0),
    [itemsOf]
  )

  const subtotalOf = useCallback(
    (mode: OrderType) =>
      itemsOf(mode).reduce((sum, i) => sum + unitPriceOf(i.product, mode) * i.quantity, 0),
    [itemsOf, unitPriceOf]
  )

  const shippingOf = useCallback(
    (mode: OrderType) => {
      if (mode === 'SELL') return 0
      return subtotalOf(mode) >= 50000000 ? 0 : 500000
    },
    [subtotalOf]
  )

  const totalOf = useCallback(
    (mode: OrderType) => subtotalOf(mode) + shippingOf(mode),
    [subtotalOf, shippingOf]
  )

  return (
    <CartContext.Provider
      value={{
        items,
        itemsOf,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        clearMode,
        countOf,
        subtotalOf,
        shippingOf,
        totalOf,
        unitPriceOf
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
